import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import { Activity, BarChart3, Building2, Package } from 'lucide-react';
import clsx from 'clsx';
import { useOrderStats } from '../../hooks/useOrders';
import { useUrnResolver } from '../../hooks/useUrnResolver';
import type { OrderStatsParams, TimelineBucket } from '../../api/orders';

const STATUS_COLORS: Record<string, string> = {
  completed: '#10b981', processing: '#f59e0b', pending: '#f59e0b', queued: '#8b5cf6',
  created: '#3b82f6', failed: '#ef4444', error: '#dc2626', canceled: '#6b7280',
  canceling: '#6b7280', 'cancel.submitted': '#6b7280', declined: '#f43f5e',
  withdrawn: '#94a3b8', input_required: '#f97316', unknown: '#d1d5db',
};

const STATUS_ORDER = [
  'completed', 'processing', 'pending', 'queued', 'created', 'input_required',
  'failed', 'error', 'canceled', 'canceling', 'cancel.submitted', 'declined', 'withdrawn',
];

const PALETTE = ['#8b5cf6', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#f97316', '#6366f1', '#14b8a6'];

const TIMELINE_SERIES = [
  { key: 'completed', label: 'Completed', color: '#10b981' },
  { key: 'processing', label: 'Processing', color: '#f59e0b' },
  { key: 'failed', label: 'Failed', color: '#ef4444' },
  { key: 'other', label: 'Other', color: '#8b5cf6' },
] as const;

type TimelineSeriesKey = (typeof TIMELINE_SERIES)[number]['key'];
type ViewMode = 'timeline' | 'status' | 'products' | 'lenders';

const VIEWS: { key: ViewMode; label: string; icon: typeof Activity }[] = [
  { key: 'timeline', label: 'Timeline', icon: Activity },
  { key: 'status', label: 'By Status', icon: BarChart3 },
  { key: 'products', label: 'By Product', icon: Package },
  { key: 'lenders', label: 'By Lender', icon: Building2 },
];

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24);
  if (diffDays < 1.5) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays < 8) return d.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function TimelineTooltip({ active, payload, label, visibleSeries }: {
  active?: boolean;
  payload?: { name: string; value: number; color: string; dataKey: string }[];
  label?: string;
  visibleSeries: Set<TimelineSeriesKey>;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 px-3 py-2.5 rounded shadow-lg border border-gray-200 dark:border-gray-700 text-xs">
      <p className="font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label ? new Date(label).toLocaleString() : ''}</p>
      {payload.filter((p) => visibleSeries.has(p.dataKey as TimelineSeriesKey)).map((e) => (
        <div key={e.name} className="flex items-center gap-2 py-0.5">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />
          <span className="text-gray-600 dark:text-gray-300 capitalize">{e.name}</span>
          <span className="ml-auto font-semibold text-gray-900 dark:text-gray-100">{e.value}</span>
        </div>
      ))}
    </div>
  );
}

function BarTooltip({ active, payload }: {
  active?: boolean;
  payload?: { value: number; payload: { name: string; fill?: string } }[];
}) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div className="bg-white dark:bg-gray-800 px-3 py-2.5 rounded shadow-lg border border-gray-200 dark:border-gray-700 text-xs">
      <div className="flex items-center gap-2 mb-0.5">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.payload?.fill || '#6366f1' }} />
        <span className="font-medium text-gray-900 dark:text-gray-100">{item.payload.name}</span>
      </div>
      <span className="text-gray-500 dark:text-gray-400">{item.value.toLocaleString()} orders</span>
    </div>
  );
}

interface OrdersChartProps {
  from?: string;
  to?: string;
  buckets: TimelineBucket[];
  timelineLoading?: boolean;
  originator?: string;
}

export default function OrdersChart({ from, to, buckets, timelineLoading, originator }: OrdersChartProps) {
  const navigate = useNavigate();
  const { resolveProductUrn, resolveLenderUrn } = useUrnResolver();

  const [view, setView] = useState<ViewMode>('timeline');
  const [visibleSeries, setVisibleSeries] = useState<Set<TimelineSeriesKey>>(
    new Set(TIMELINE_SERIES.map((s) => s.key)),
  );

  const statsParams = useMemo<OrderStatsParams>(() => {
    const p: OrderStatsParams = {};
    if (from) p.from = from;
    if (to) p.to = to;
    if (originator) p.originator = originator;
    return p;
  }, [from, to, originator]);
  const { data: stats, isLoading: statsLoading } = useOrderStats(statsParams);

  const statusData = useMemo(() => {
    if (!stats?.byStatus) return [];
    return STATUS_ORDER.filter((s) => stats.byStatus[s])
      .map((s) => ({ name: s.replace(/[._]/g, ' '), rawStatus: s, value: stats.byStatus[s], fill: STATUS_COLORS[s] || '#d1d5db' }));
  }, [stats]);

  const productData = useMemo(() => {
    if (!stats?.byProduct) return [];
    return Object.entries(stats.byProduct).sort(([, a], [, b]) => b - a).slice(0, 10)
      .map(([urn, count], i) => ({ name: resolveProductUrn(urn), fullName: urn, value: count, fill: PALETTE[i % PALETTE.length] }));
  }, [stats, resolveProductUrn]);

  const lenderData = useMemo(() => {
    if (!stats?.byOriginator) return [];
    return Object.entries(stats.byOriginator).sort(([, a], [, b]) => b - a).slice(0, 10)
      .map(([urn, count], i) => ({ name: resolveLenderUrn(urn), fullName: urn, value: count, fill: PALETTE[i % PALETTE.length] }));
  }, [stats, resolveLenderUrn]);

  const timelineData = useMemo(() => buckets.map((b) => ({ ...b, label: formatTime(b.time) })), [buckets]);

  const inlineStats = useMemo(() => {
    if (!buckets.length) return null;
    const t = { completed: 0, processing: 0, failed: 0, other: 0, total: 0 };
    for (const b of buckets) { t.completed += b.completed; t.processing += b.processing; t.failed += b.failed; t.other += b.other; t.total += b.total; }
    return t;
  }, [buckets]);

  const toggleSeries = (key: TimelineSeriesKey) => {
    setVisibleSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); } else { next.add(key); }
      return next;
    });
  };

  const handleBarClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => {
      if (view === 'status' && data?.rawStatus) navigate(`/orders?status=${encodeURIComponent(data.rawStatus)}`);
      else if (view === 'products' && data?.fullName) navigate(`/orders?product=${encodeURIComponent(data.fullName)}`);
      else if (view === 'lenders' && data?.fullName) navigate(`/orders?originator=${encodeURIComponent(data.fullName)}`);
    },
    [view, navigate],
  );

  const activeBarData = view === 'status' ? statusData : view === 'products' ? productData : lenderData;
  const isLoading = view === 'timeline' ? timelineLoading : statsLoading;

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Order Insights</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {view === 'timeline'
              ? `${inlineStats?.total.toLocaleString() ?? 0} orders in period`
              : `${(stats?.total ?? 0).toLocaleString()} total orders`}
          </p>
        </div>
        <div className="inline-flex items-center rounded bg-gray-100 dark:bg-gray-800/80 p-0.5">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={clsx(
                'inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md transition-all whitespace-nowrap',
                v.key === view
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
              )}
            >
              <v.icon className="h-3 w-3" />
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Timeline series toggles */}
      {view === 'timeline' && (
        <div className="px-5 py-2.5 border-b border-gray-50 dark:border-gray-800/50 flex items-center gap-4 flex-wrap">
          {TIMELINE_SERIES.map((s) => {
            const isActive = visibleSeries.has(s.key);
            return (
              <button
                key={s.key}
                onClick={() => toggleSeries(s.key)}
                className={clsx(
                  'inline-flex items-center gap-1.5 text-xs transition-all',
                  isActive ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500',
                )}
              >
                <span
                  className="w-2.5 h-2.5 rounded-sm"
                  style={{ backgroundColor: s.color, opacity: isActive ? 1 : 0.3 }}
                />
                {s.label}
                {inlineStats && (
                  <span className="text-gray-400 dark:text-gray-500 font-normal">{inlineStats[s.key]}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Chart */}
      <div className="p-4 lg:p-5">
        {isLoading ? (
          <div className="h-[340px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" />
          </div>
        ) : view === 'timeline' ? (
          !timelineData.length ? (
            <div className="h-[340px] flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
              No order activity in this time range
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <AreaChart data={timelineData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <defs>
                  {TIMELINE_SERIES.map((s) => (
                    <linearGradient key={s.key} id={`grad_${s.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={s.color} stopOpacity={0.15} />
                      <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-800" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<TimelineTooltip visibleSeries={visibleSeries} />} />
                {TIMELINE_SERIES.map((s) => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    stroke={visibleSeries.has(s.key) ? s.color : 'transparent'}
                    fill={visibleSeries.has(s.key) ? `url(#grad_${s.key})` : 'transparent'}
                    strokeWidth={visibleSeries.has(s.key) ? 2 : 0}
                    dot={false}
                    activeDot={visibleSeries.has(s.key) ? { r: 3, strokeWidth: 0 } : false}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )
        ) : (
          !activeBarData.length ? (
            <div className="h-[340px] flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
              No data for this view
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-3">
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={activeBarData} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} className="stroke-gray-200 dark:stroke-gray-800" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                    <Tooltip content={<BarTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22} className="cursor-pointer" onClick={handleBarClick}>
                      {activeBarData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} className="cursor-pointer" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="lg:col-span-2 flex flex-col items-center justify-center">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={activeBarData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}
                      dataKey="value" nameKey="name" stroke="none"
                      onClick={(_, idx) => handleBarClick(activeBarData[idx])} className="cursor-pointer"
                    >
                      {activeBarData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} className="cursor-pointer" />
                      ))}
                    </Pie>
                    <Tooltip content={<BarTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-1.5 justify-center mt-2 px-2 max-w-xs">
                  {activeBarData.slice(0, 8).map((entry) => (
                    <button
                      key={entry.name}
                      onClick={() => handleBarClick(entry)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                    >
                      <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: entry.fill }} />
                      <span className="truncate max-w-[80px]">{entry.name}</span>
                      <span className="font-semibold">{entry.value}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
